import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Box,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Button,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Logo, N, navDropdownMenuProps } from "./LandingPage";
import LoginModal from "../components/Modals/LoginModal";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loginModalOpen, setLoginModalOpen] = useState(true);
  const userType = searchParams.get('type') || 'staff'; // Default to staff if no type specified
  const isStudent = userType === 'student';
  const isLoginRoute = ((): boolean => {
    try {
      if (location && typeof location.pathname === 'string' && location.pathname.includes('/login')) return true;
      if (location && typeof (location as any).hash === 'string' && (location as any).hash.includes('/login')) return true;
      if (typeof window !== 'undefined' && window.location && window.location.href.includes('/#/login')) return true;
    } catch (e) {}
    return false;
  })();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Inject Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,400&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400&display=swap';
    document.head.appendChild(link);
    return () => {
      try {
        document.head.removeChild(link);
      } catch {}
    };
  }, []);

  useEffect(() => {
    // Ensure modal is open when component mounts
    setLoginModalOpen(true);
  }, []);

  const navLinks = [
    { label: t('landing_nav_features'), href: '#features' },
    { label: t('landing_nav_platform'), href: '#modules'  },
    { label: t('landing_nav_schools'),  href: '#schools'  },
    { label: t('landing_nav_about'),    href: '#about'    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: N.bg,
        minHeight: "100vh",
        fontFamily: "'DM Sans', sans-serif",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* Navbar */}
      <Box
        component="nav"
        sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, md: '4rem' }, py: '1rem',
          background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${N.gray200}`,
        }}
      >
        <Box
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => navigate('/')}
        >
          <Logo gradId="nav-g" variant="dark" width={isMobile ? 120 : 176} height={isMobile ? 26 : 38} />
        </Box>

        {!isMobile && !isLoginRoute && (
          <Stack
            direction="row"
            spacing={4}
            component="ul"
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {navLinks.map(({ label, href }) => (
              <Box component="li" key={label}>
                <Box
                  component="a"
                  href={`/${href}`}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/' + href);
                  }}
                  sx={{
                    color: N.gray600, textDecoration: 'none',
                    fontSize: '0.875rem', fontWeight: 500,
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    transition: 'color 0.2s', cursor: 'pointer',
                    '&:hover': { color: N.navy },
                  }}
                >
                  {label}
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl
            sx={{
              display: { xs: 'none', md: 'block' },
              backgroundColor: N.navy,
              borderRadius: '2px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 16px rgba(11,29,110,0.25)`,
                '& .MuiOutlinedInput-root': { backgroundColor: N.navyMid },
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: N.navy, color: N.white,
                borderRadius: '2px', fontWeight: 600, fontSize: '0.8rem',
                letterSpacing: '0.04em', textTransform: 'uppercase',
                transition: 'background-color 0.2s',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none', background: 'transparent' },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none', background: 'transparent' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none', background: 'transparent' },
                '& fieldset': { border: 'none', background: 'transparent' },
                '& .MuiSelect-select': { background: 'transparent' },
              },
              '& .MuiSelect-icon': { color: N.white, background: 'transparent' },
              '& .MuiSelect-select': { py: '0.55rem', px: '1.25rem', lineHeight: 1.75 },
            }}
          >
            <Select
              value={i18n.language}
              onChange={(e) => {
                const lang = e.target.value;
                i18n.changeLanguage(lang);
                try { localStorage.setItem('selectedLanguage', lang); localStorage.setItem('i18nextLng', lang); } catch {}
              }}
              MenuProps={navDropdownMenuProps}
            >
              <MenuItem value="hi">हिन्दी</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={() => navigate(isStudent ? '/login?type=staff' : '/login?type=student')}
            sx={{
              background: N.navy, color: N.white,
              fontWeight: 600, fontSize: '0.875rem',
              px: { xs: 1.5, md: '1.5rem' }, py: '0.485rem',
              borderRadius: '2px', letterSpacing: '0.04em',
              textTransform: 'uppercase', minWidth: 0,
              transition: 'all 0.2s',
              '&:hover': {
                background: N.navyMid,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 16px rgba(11,29,110,0.25)',
              },
            }}
          >
            {isStudent ? "School Login" : "Student/Parent Login"}
          </Button>

          {isMobile && (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: N.gray600, p: 0.5 }}>
              <MenuIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { background: N.navy, color: N.white, width: 260, p: 3 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Logo gradId="mob-g" variant="light" width={140} height={30} />
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Stack spacing={0.5}>
          {!isLoginRoute && navLinks.map(({ label, href }) => (
            <Box
              key={label}
              component="a"
              href={`/${href}`}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setMobileOpen(false);
                navigate('/' + href);
              }}
              sx={{
                display: 'block', color: 'rgba(255,255,255,0.75)', textDecoration: 'none',
                py: 1.5, px: 2, borderRadius: 1,
                fontSize: '0.9rem', fontWeight: 500,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                transition: 'all 0.2s', cursor: 'pointer',
                '&:hover': { color: N.white, background: 'rgba(255,255,255,0.08)' },
              }}
            >
              {label}
            </Box>
          ))}
          <Box sx={{ pt: 2, mt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                mb: 1.5,
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '2px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent', color: N.white,
                  borderRadius: '2px', fontWeight: 600, fontSize: '0.85rem',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none', background: 'transparent' },
                  '& fieldset': { border: 'none', background: 'transparent' },
                  '& .MuiSelect-select': { background: 'transparent' },
                },
                '& .MuiSelect-icon': { color: N.white, background: 'transparent' },
              }}
            >
              <Select
                value={i18n.language}
                onChange={(e) => {
                  const lang = e.target.value;
                  i18n.changeLanguage(lang);
                  try { localStorage.setItem('selectedLanguage', lang); localStorage.setItem('i18nextLng', lang); } catch {}
                }}
                MenuProps={navDropdownMenuProps}
              >
                <MenuItem value="hi">हिन्दी</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setMobileOpen(false);
                navigate(isStudent ? '/login?type=staff' : '/login?type=student');
              }}
              sx={{
                background: N.amber, color: N.navy, fontWeight: 700,
                borderRadius: '2px', textTransform: 'none', py: 1.2,
                '&:hover': { background: N.amberLight },
              }}
            >
              {isStudent ? "School Login" : "Student/Parent Login"}
            </Button>
          </Box>
        </Stack>
      </Drawer>

      {/* Main Container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pt: "80px", // space for fixed navbar
          pb: 4,
          px: 2,
        }}
      >
        <LoginModal
          key={userType}
          open={loginModalOpen}
          onClose={() => {
            setLoginModalOpen(false);
            navigate("/");
          }}
          showRegister={isStudent}
          isStudent={isStudent}
        />
      </Box>
    </Box>
  );
};

export default Login;
